import Head from "next/head";
import { useState } from "react";
import { Zap, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";

export default function RwgBridgeForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await fetch("/api/auth/forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    setLoading(false);
    if (res.ok || res.status === 404) {
      // Always show success to avoid email enumeration
      setSent(true);
    } else {
      const d = await res.json().catch(() => ({}));
      setError(d.message ?? "Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <Head>
        <title>Reset password · RWG Bridge</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-200">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Forgot password?</h1>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            {sent ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Check your inbox</p>
                  <p className="mt-1 text-sm text-gray-500">
                    If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset link.
                  </p>
                </div>
                <a
                  href="/rwg-bridge/login"
                  className="mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </a>
              </div>
            ) : (
              <>
                <p className="mb-5 text-sm text-gray-500">
                  Enter your email and we&apos;ll send you a link to reset your password.
                </p>

                {error && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Email address</label>
                    <input
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="you@example.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60 transition-colors">
                    {loading ? "Sending…" : "Send reset link"}
                  </button>
                </form>

                <a
                  href="/rwg-bridge/login"
                  className="mt-5 flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
